import React, { useMemo } from 'react';
import DecisionEngineBlock from '../../decision-engine/DecisionEngineBlock';
import {
  aksiyonKilidiHesapla,
  aksiyonSkoruHesapla,
  aksiyonlaraKilitUygula,
  guvenBandiHesapla
} from '../../decision-engine/decisionEngineScoring';
import DrawerFooterActions from './DrawerFooterActions';
import { olusturAksiyonDurumKapsami } from '../../decision-engine/actionStateScope';

function Row({ label, value }) {
  return (
    <li className="flex items-center justify-between text-sm">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="font-black text-slate-900 dark:text-white">{value}</span>
    </li>
  );
}

const formatTry = (value) => `${Number(value || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} TL`;

export default function EconomyDetailDrawer({ entity, onClose }) {
  const payload = entity?.payload || {};
  const economy = payload?.economy || payload;
  const summary = economy?.summary || {};
  const periodBreakdown = economy?.periodBreakdown || {};
  const trends = Array.isArray(economy?.trends) ? economy.trends : [];
  const sampleSize = Number(periodBreakdown?.month?.createdOrders || 0);

  const topDays = [...trends]
    .sort((a, b) => Number(b?.totalValueTry || 0) - Number(a?.totalValueTry || 0))
    .slice(0, 10);

  const decisionLayer = useMemo(() => {
    const conversion = Number(summary.shoppingConversionRate || 0);
    const monthlyValue = Number(summary.monthlyValueTry || 0);
    const aov = Number(summary.aovTry || 0);
    const createdMonth = Number(periodBreakdown?.month?.createdOrders || 0);
    const completedMonth = Number(periodBreakdown?.month?.completedOrders || 0);

    const confidence = guvenBandiHesapla(sampleSize, { yuksek: 120, orta: 40 });

    const anomalies = [];
    if (conversion > 100) {
      anomalies.push({
        title: 'Alışveriş dönüşümü %100 üzerinde',
        meaning: 'Dönüşüm oranı matematiksel sınırı aşıyor.',
        risk: 'Dönüşüm hesabında olay eşleşmesi veya payda hatası olabilir.',
        recommendation: 'Sipariş oluşturma ve tamamlama olayları aynı pencerede yeniden doğrulanmalı.'
      });
    }
    if (completedMonth > createdMonth) {
      anomalies.push({
        title: 'Tamamlanan sipariş, oluşturulandan yüksek',
        meaning: 'Aylık sipariş akışında sıralama tutarsız görünüyor.',
        risk: 'Tekilleştirme veya zaman penceresi hatası kararları yanıltabilir.',
        recommendation: 'Aylık sipariş olay sırası ve tekilleştirme kuralı kontrol edilmeli.'
      });
    }
    if (monthlyValue === 0 && createdMonth > 0) {
      anomalies.push({
        title: 'Sipariş var ama aylık değer 0',
        meaning: 'Sipariş akışı var, değer eşleşmesi görünmüyor.',
        risk: 'Tutar entegrasyonu eksikse ekonomi kararları hatalı olur.',
        recommendation: 'Sipariş tutar eşleşmesi ve para birimi dönüşümü doğrulanmalı.'
      });
    }

    const { actionLock, actionLockReason } = aksiyonKilidiHesapla({
      anomaliSayisi: anomalies.length,
      guvenBandi: confidence,
      anomaliMesaji: 'Bu aksiyon şu an uygulanamaz: veri tutarsızlığı giderilmeden canlıya alınmamalı.',
      dusukOrneklemMesaji: 'Bu aksiyon şu an uygulanamaz: örneklem düşük, önce gözlem modunda izlenmeli.'
    });

    const actions = [
      {
        actionKey: 'checkout-surtunme-azalt',
        title: 'Checkout adımlarındaki sürtünmeyi azalt',
        reason: 'Oluşturulan sipariş ile tamamlanan sipariş arasında kayıp var.',
        doNotApplyWhen: 'Aylık sipariş akışında tutarsızlık alarmı varsa önce veri doğrulanmalı.',
        expectedImpact: 'Tamamlama oranında artış',
        affectedSegment: 'Alışverişe geçen kullanıcı kitlesi',
        score: aksiyonSkoruHesapla(8, confidence, sampleSize, { yuksek: 120, orta: 40 }),
        enabled: !actionLock,
        lockReason: actionLockReason,
        impacts: ['+%6 Sipariş tamamlama', '+%3 Alışveriş dönüşümü', '+140 kullanıcı etki alanı'],
        baselineExpected: {
          metric: 'Alışveriş dönüşümü',
          baseline: `%${conversion.toLocaleString('tr-TR')}`,
          expected: `%${Number((conversion + 3).toFixed(2)).toLocaleString('tr-TR')}`
        },
        context: {
          orneklem: sampleSize,
          segment: 'Genel Kitle',
          yemekTipi: 'Genel',
          pencere: 'Son 30 Gün'
        },
        status: 'uygulanmadı',
        resultDelta: '',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: [
          'Sipariş oluşturma var, tamamlamada düşüş bulunuyor.',
          'Checkout sürtünmesi azaldığında dönüşüm ve gelir birlikte artar.'
        ]
      },
      {
        actionKey: 'aov-tamamlayici-eslestirme',
        title: 'Düşük AOV segmentlerinde tamamlayıcı ürün eşleştirmesi aç',
        reason: 'Ortalama sepet değeri artırılabilir.',
        doNotApplyWhen: 'AOV değeri veri gecikmesi nedeniyle güncel değilse canlıya alınmamalı.',
        expectedImpact: 'AOV artışı',
        affectedSegment: 'Düşük sepet değerine sahip kullanıcılar',
        score: aksiyonSkoruHesapla(6, confidence, sampleSize, { yuksek: 120, orta: 40 }),
        enabled: !actionLock,
        lockReason: actionLockReason,
        impacts: ['+%4 AOV', '+%2 Aylık değer'],
        baselineExpected: {
          metric: 'AOV',
          baseline: formatTry(aov),
          expected: formatTry(aov * 1.04)
        },
        context: {
          orneklem: sampleSize,
          segment: 'Düşük AOV Segmenti',
          yemekTipi: 'Genel',
          pencere: 'Son 30 Gün'
        },
        status: 'testte',
        resultDelta: '+%1.1 AOV',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: ['AOV ekonomik değeri doğrudan etkiliyor.', 'Tamamlayıcı ürün önerisi düşük riskle değer artırır.']
      },
      {
        actionKey: 'kampanya-takvim-paterni',
        title: 'Yüksek değerli gün paternini kampanya takvimine taşı',
        reason: 'Trend günlerinde değer üretimi belirgin şekilde daha yüksek.',
        doNotApplyWhen: 'Trend verisi 10 günden azsa patern kararı ertelenmeli.',
        expectedImpact: 'Aylık toplam değerde artış',
        affectedSegment: 'Kampanya döneminde aktif kullanıcılar',
        score: aksiyonSkoruHesapla(5, confidence, sampleSize, { yuksek: 120, orta: 40 }),
        enabled: !actionLock,
        lockReason: actionLockReason,
        impacts: ['+%5 Aylık ekonomik değer', '+%2 Dönüşüm'],
        baselineExpected: {
          metric: 'Aylık değer',
          baseline: formatTry(monthlyValue),
          expected: formatTry(monthlyValue * 1.05)
        },
        context: {
          orneklem: sampleSize,
          segment: 'Kampanya Kitlesi',
          yemekTipi: 'Genel',
          pencere: 'Son 30 Gün'
        },
        status: 'uygulanmadı',
        resultDelta: '',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: ['Yüksek değerli günlerde satın alma eğilimi artıyor.', 'Takvim eşleşmesi bütçe verimini yükseltir.']
      }
    ];

    const actionsWithLock = aksiyonlaraKilitUygula(actions, actionLock, actionLockReason);
    const priority = conversion >= 35 ? 'HIGH' : conversion >= 15 ? 'MEDIUM' : 'LOW';
    const keyTakeaway = conversion < 20
      ? 'Ekonomi tarafında en büyük fırsat, checkout kaybını azaltarak dönüşümü yükseltmek.'
      : 'Ekonomi tarafında en hızlı etki, AOV ve tamamlanma hattını birlikte optimize etmek.';

    return {
      narrative: 'Ekonomi görünümü sipariş akışı, dönüşüm ve değer trendi birlikte okunarak bütçe etkisi yüksek alanları önceliklendirir.',
      keyTakeaway,
      actions: actionsWithLock,
      anomalies,
      priority,
      confidence,
      sampleSize,
      expectedOutcome: 'Dönüşüm darboğazları iyileştirilirse aylık ekonomik değerde +%5 ila +%12 artış hedeflenir.',
      actionLock,
      actionLockReason
    };
  }, [summary, periodBreakdown, sampleSize]);

  const actionStateScope = olusturAksiyonDurumKapsami('economy', entity);

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ekonomi-detay-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <DecisionEngineBlock
        narrative={decisionLayer.narrative}
        keyTakeaway={decisionLayer.keyTakeaway}
        actions={decisionLayer.actions}
        anomalies={decisionLayer.anomalies}
        priority={decisionLayer.priority}
        confidence={decisionLayer.confidence}
        sampleSize={decisionLayer.sampleSize}
        expectedOutcome={decisionLayer.expectedOutcome}
        actionLock={decisionLayer.actionLock}
        actionLockReason={decisionLayer.actionLockReason}
        actionStateScope={actionStateScope}
      />

      <section className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
        <p className="text-xs text-slate-500 dark:text-slate-400">Ekonomi Özet Alanı</p>
        <h4 className="mt-1 text-lg font-black text-slate-900 dark:text-white">{formatTry(summary.monthlyValueTry)}</h4>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Dönüşüm: {Number(summary.shoppingConversionRate || 0).toLocaleString('tr-TR')}% | AOV: {formatTry(summary.aovTry)}
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Dönem Kırılımı</p>
        <ul className="mt-2 space-y-1.5">
          <Row label="Bu ay oluşturulan sipariş" value={Number(periodBreakdown?.month?.createdOrders || 0).toLocaleString('tr-TR')} />
          <Row label="Bu ay tamamlanan sipariş" value={Number(periodBreakdown?.month?.completedOrders || 0).toLocaleString('tr-TR')} />
          <Row label="Bu yıl oluşturulan sipariş" value={Number(periodBreakdown?.year?.createdOrders || 0).toLocaleString('tr-TR')} />
          <Row label="Bu yıl tamamlanan sipariş" value={Number(periodBreakdown?.year?.completedOrders || 0).toLocaleString('tr-TR')} />
        </ul>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">En Yüksek Değerli Günler (İlk 10)</p>
        {topDays.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Bu filtrede trend kaydı bulunamadı.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {topDays.map((row) => (
              <li key={row.dayDate} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-slate-700 dark:text-slate-200">{row.dayDate}</span>
                <span className="font-black text-slate-900 dark:text-white">{formatTry(row.totalValueTry)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <DrawerFooterActions onClose={onClose} onExport={handleExport} />
    </div>
  );
}


